import { Box, type BoxProps } from '@chakra-ui/react';

export type ProgressiveBlurEdge = 'top' | 'bottom';

export type ProgressiveBlurProps = Omit<BoxProps, 'children'> & {
  /** Strong blur at this edge, fading to clear toward the opposite side. */
  edge?: ProgressiveBlurEdge;
  /** Backdrop blur radius in CSS pixels. */
  blur?: number;
  /**
   * Keeps color in the frost. Blur alone turns art into gray mud, which reads
   * as a stain on a light page. 1 leaves the color alone.
   */
  saturate?: number;
};

const EDGE_MASK: Record<ProgressiveBlurEdge, string> = {
  // Same curve as VictoryPointPanel / CollectionRecap progressive edges.
  top: 'linear-gradient(to bottom, black 0%, rgba(0,0,0,0.8) 42%, transparent 100%)',
  bottom: 'linear-gradient(to top, black 0%, rgba(0,0,0,0.8) 42%, transparent 100%)',
};

/**
 * Masked backdrop blur that tapers off — strong at `edge`, clear opposite.
 * Prefer this over a uniform `backdrop-filter` on a chrome surface.
 */
export function ProgressiveBlur({
  edge = 'top',
  blur = 14,
  saturate = 1,
  bg = 'bg/8',
  pointerEvents = 'none',
  'aria-hidden': ariaHidden = true,
  zIndex = 0,
  top,
  bottom,
  ...boxProps
}: ProgressiveBlurProps) {
  const mask = EDGE_MASK[edge];
  const blurValue = saturate === 1 ? `blur(${blur}px)` : `blur(${blur}px) saturate(${saturate})`;

  return (
    <Box
      aria-hidden={ariaHidden}
      position="absolute"
      insetX={0}
      top={top ?? (edge === 'top' ? 0 : undefined)}
      bottom={bottom ?? (edge === 'bottom' ? 0 : undefined)}
      bg={bg}
      pointerEvents={pointerEvents}
      zIndex={zIndex}
      {...boxProps}
      css={{
        backdropFilter: blurValue,
        WebkitBackdropFilter: blurValue,
        maskImage: mask,
        WebkitMaskImage: mask,
        ...(typeof boxProps.css === 'object' && boxProps.css !== null ? boxProps.css : null),
      }}
    />
  );
}
